import { Grid } from '@mui/material';
import { Formik } from 'formik';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from 'react-query';
import {
	CerebroDrawer,
	CerebroForm,
	CerebroLabel,
	CerebroPrimaryButton,
	CerebroRichText,
} from '../../shared/components';
import { useApi } from '../../shared/context/ApiContext';
import { QueryKeys } from '../../shared/hooks';
import { DataRoom, UpdateDataRoomDto } from '../../shared/models';
import { FormikUtils } from '../../shared/utils';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { getApiResponseData } from '../../shared/utils/axios';

export interface ICustomNdaProps {
	dataRoom: DataRoom;
	close: () => void;
	open: boolean;
}

export const CustomNdaForm: React.FC<ICustomNdaProps> = ({ dataRoom, close, open }) => {
	const dataRoomId = dataRoom.guid;
	const api = useApi();
	const snackbar = useSnackbar();
	const queryClient = useQueryClient();

	const customNdaMutation = useMutation(
		async ({ values }: CustomNdaFormVariables) => {
			const dataRoomPatch: UpdateDataRoomDto = {
				customAgreementText: values.customAgreementText,
				page: 'Summary',
			};

			const { data, error } = await getApiResponseData<void>(
				api.patch(CerebroApiEndpoints.DataRooms.Root(dataRoomId), dataRoomPatch)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: () => {
				queryClient.invalidateQueries(QueryKeys.DataRoom.Root(dataRoomId));
				snackbar.enqueueSnackbar('Custom Agreement updated.', { variant: 'success' });
				close();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, { variant: 'error' });
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroDrawer open={open} onClose={() => close()} PaperProps={{ sx: { width: '900px' } }}>
			<Formik
				initialValues={{
					customAgreementText: dataRoom.customAgreementText,
				}}
				onSubmit={FormikUtils.MutateOnSubmit(customNdaMutation.mutate)}
			>
				{({ isSubmitting, handleSubmit, values, setFieldValue }) => {
					return (
						<CerebroForm onSubmit={handleSubmit}>
							<Grid
								container
								spacing={3}
								sx={{ marginTop: 3 }}
								justifyContent={{ xs: 'center', md: 'flex-start' }}
							>
								<Grid item xs={12}>
									<CerebroLabel id="agreementText">Custom Agreement Text</CerebroLabel>
									<CerebroRichText
										placeholder={'Enter custom agreement text here...'}
										value={values.customAgreementText ?? ''}
										onChange={updatedValue => setFieldValue('customAgreementText', updatedValue)}
									/>
								</Grid>

								<Grid item xs={8}>
									<CerebroPrimaryButton loading={isSubmitting} type="submit">
										Save
									</CerebroPrimaryButton>
								</Grid>
							</Grid>
						</CerebroForm>
					);
				}}
			</Formik>
		</CerebroDrawer>
	);
};

type CustomNdaFormVariables = FormikUtils.MutateOnSubmitVariables<CustomNdaFormValues>;
interface CustomNdaFormValues {
	customAgreementText: string;
}
